import { MonthlyIncidentCount } from "../../../types/insightDashboard";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../ui/card";
import {
  ChartConfig,
  ChartContainer,
  ChartLegend,
  ChartTooltip,
  ChartTooltipContent,
} from "../../ui/chart";
import { CHART_MARGIN_TOP } from "../chartStyles";
import { formatNumber } from "../utils";
import { InsightLegendContent } from "./InsightLegendContent";

interface ComparisonAreaChartProps {
  title: string;
  description?: string;
  focusData: MonthlyIncidentCount[];
  comparisonData: MonthlyIncidentCount[];
  focusYear: number;
  comparisonYear: number;
  descriptionTerm?: string;
  focusFillOpacity?: number;
  comparisonFillOpacity?: number;
}

export function ComparisonAreaChart({
  title,
  description,
  focusData,
  comparisonData,
  focusYear,
  comparisonYear,
  descriptionTerm = "monthly activity",
  focusFillOpacity = 0,
  comparisonFillOpacity = 0.4,
}: ComparisonAreaChartProps) {
  const chartData = focusData.map((focusMonthData, index) => {
    const comparisonMonthData = comparisonData[index];
    return {
      month: focusMonthData.month,
      focusYearData: focusMonthData.isFutureMonth ? null : focusMonthData.value,
      comparisonYearData: comparisonMonthData?.isFutureMonth
        ? null
        : (comparisonMonthData?.value ?? null),
    };
  });

  const chartConfig = {
    focusYearData: {
      label: String(focusYear),
      color: "var(--chart-1)",
    },
    comparisonYearData: {
      label: String(comparisonYear),
      color: "var(--chart-2)",
    },
  } satisfies ChartConfig;

  return (
    <Card className="tw:h-full">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        <CardDescription>
          {description ||
            `Comparison of ${descriptionTerm} between ${focusYear} and ${comparisonYear}`}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ChartContainer config={chartConfig} className="tw:aspect-auto tw:h-[250px] tw:w-full">
          <AreaChart
            accessibilityLayer
            data={chartData}
            margin={{
              top: CHART_MARGIN_TOP,
              left: 12,
              right: 12,
            }}
          >
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="month"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={(value) => value.slice(0, 3)}
            />
            <YAxis allowDecimals={false} tickFormatter={(value: number) => formatNumber(value)} />{" "}
            <ChartTooltip cursor={false} content={<ChartTooltipContent indicator="dot" />} />{" "}
            <Area
              dataKey="focusYearData"
              type="linear"
              fill="var(--color-focusYearData)"
              fillOpacity={focusFillOpacity}
              stroke="var(--color-focusYearData)"
              strokeWidth={2}
            />
            <Area
              dataKey="comparisonYearData"
              type="linear"
              fill="var(--color-comparisonYearData)"
              fillOpacity={comparisonFillOpacity}
              stroke="none"
            />
            <ChartLegend content={<InsightLegendContent />} />
          </AreaChart>
        </ChartContainer>
      </CardContent>
    </Card>
  );
}
